/**
 * useAnimatedNumber — плавно анимирует displayed value к target.
 *
 * Применение: AwarenessCounter после кристаллизации получает крупный
 * прирост (например +25 духобаллов сразу). Без анимации число просто
 * прыгает — момент награды теряется. С анимацией игрок видит «count
 * up» которое идёт ~700ms и читается как реальная награда.
 *
 * Поведение:
 *  - Если delta < `instantThreshold` — обновляется мгновенно (tap отклик
 *    не должен лагать; +1 каждые 250ms = noisy если анимировать).
 *  - Если delta >= `instantThreshold` — анимирует на `duration` ms через
 *    requestAnimationFrame с easeOutQuart (smooth deceleration).
 *  - Если target меняется ВО ВРЕМЯ анимации — перестраивается с текущей
 *    позиции на новую цель за оставшееся время (graceful chaining).
 *
 * Никаких внешних зависимостей. Cleanup на unmount.
 */
import { useEffect, useRef, useState } from 'react';

interface Options {
  /** ms на полную анимацию. Default 700. */
  readonly duration?: number;
  /** Минимальный delta для анимации. Меньше — instant. Default 5. */
  readonly instantThreshold?: number;
}

/**
 * Easing — ease-out quartic. Быстрый старт, плавное замедление, читается
 * как «прирост влетает и оседает».
 */
function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4);
}

export function useAnimatedNumber(target: number, opts: Options = {}): number {
  const duration = opts.duration ?? 700;
  const instantThreshold = opts.instantThreshold ?? 5;

  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const delta = Math.abs(target - display);
    // Small delta — skip animation, set directly.
    if (delta < instantThreshold) {
      setDisplay(target);
      return;
    }
    // 2026-05-26 BUG FIX: negative delta (target < display) — instant
    // reset. Это происходит при crystallize: awarenesses 100 → 0. Без
    // этого игрок видел как счётчик «откатывается» назад за 700ms что
    // выглядело как «потерял», а не «конвертировал».
    if (target < display) {
      setDisplay(target);
      return;
    }

    // Capture starting point (current display, не предыдущий target).
    fromRef.current = display;
    startRef.current = performance.now();

    const tick = (now: number): void => {
      if (startRef.current === null) return;
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutQuart(t);
      const value = fromRef.current + (target - fromRef.current) * eased;
      setDisplay(t >= 1 ? target : Math.round(value));
      if (t < 1) {
        rafRef.current = window.requestAnimationFrame(tick);
      } else {
        startRef.current = null;
        rafRef.current = null;
      }
    };

    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      startRef.current = null;
    };
    // Intentionally exclude `display` from deps — мы не хотим re-trigger
    // анимацию на каждый промежуточный tick (display сам обновляется
    // внутри). Только при изменении target.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration, instantThreshold]);

  return display;
}
